import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import HeaderIcon from '../../assets/note.svg';
import { useStore } from '../../contexts/StoreContext';
import { CustomerDisplay } from '../../store/LessonStore';
import Page from '../base/Page';
import AuthStore from '../../store/AuthStore';
import Api from '../../utils/Api';
import { BranchEntity } from '../@types/mysqlEntities';
import { colors } from '../const/Styles';
import CoachProfile from './display/CoachProfile';
import CustomerGridViewer from './display/CustomerGridViewer';
import WillLessonCustomers from './display/WillLessonCustomers';
import CustomerNameFilter from './surface/CustomerNameFilter';
import OutlinedInputText from '../form/OutlinedInputText';
import DominantButton from '../form/DominantButton';

type BranchFilterValue = null | number | 'UNSET';

const BRANCH_OPTION_ALL = { id: null as BranchFilterValue, label: '' };
const BRANCH_OPTION_UNSET = { id: 'UNSET' as const, label: '未設定' };

const PageLayout = styled.section`
    display: grid;
    width: 100%;
    height: 100%;
    grid-template-rows: 210px auto 1fr;
    grid-template-columns: 300px 1fr;
`;

const SearchBoxWrapper = styled.div`
    grid-column: 1 / 3;
    grid-row: 2;
    display: flex;
    align-items: center;
    padding: 10px;
    gap: 5px;
    width: 100%;
    box-sizing: border-box;
    border-bottom: 4px solid ${colors.gray};
    flex-shrink: 0;
    height: fit-content;
    min-height: fit-content;
    max-height: fit-content;
    overflow: hidden;
`;

const BranchSelect = styled.div`
    min-width: 200px;
`;

const SearchInput = styled(OutlinedInputText)`
    flex: 1;
    min-width: 0;
`;

const SearchButton = styled(DominantButton)`
    padding: 8px 16px;
    min-width: auto;
    font-size: 14px;
    background-color: #0067B2 !important;
    
    &:hover {
        background-color: #0067B2 !important;
    }
`;

const CustomerSelectWrapper = styled.section`
    grid-column: 1 / 3;
    grid-row: 3;
    overflow: hidden;
    border-top: 4px solid ${colors.gray};
    display: flex;
    min-height: 0;
`;

const LessonListPage: React.FC = () => {
    const [filteredCustomers, setFilteredCustomers] = useState<
        CustomerDisplay[]
    >([]);
    const [selectedFilterName, setSelectedFilterName] = useState<string>('ALL');
    const [searchText, setSearchText] = useState('');
    const [branches, setBranches] = useState<BranchEntity[]>([]);
    const [branchFilter, setBranchFilter] = useState<BranchFilterValue>(null);

    const { lessonStore, tempoStore } = useStore();
    const { allCustomers } = lessonStore;

    const branchOptions = useMemo(() => {
        const list = branches.map((b) => ({ id: b.id as BranchFilterValue, label: b.name }));
        return [BRANCH_OPTION_ALL, ...list, BRANCH_OPTION_UNSET];
    }, [branches]);

    const currentBranchOption = useMemo(
        () =>
            branchOptions.find((o) => o.id === branchFilter) ?? BRANCH_OPTION_ALL,
        [branchOptions, branchFilter],
    );

    useEffect(() => {
        Api.fetchBranches(AuthStore.tempoId).then(setBranches).catch(console.error);
    }, []);

    const applySearchFilter = useCallback(
        (customers: CustomerDisplay[], keyword: string): CustomerDisplay[] => {
            const normalized = keyword.trim().toLowerCase();
            if (!normalized) {
                return customers;
            }
            return customers.filter((customer) => {
                const fullName = (customer.name_sei + customer.name_mei).toLowerCase();
                const nameMatch = fullName.includes(normalized);
                const fullNameKana = (customer.name_sei_kana + customer.name_mei_kana).toLowerCase();
                const nameKanaMatch = fullNameKana.includes(normalized);
                const memberIdMatch = customer.user_name
                    ? customer.user_name.toLowerCase() === normalized
                    : false;
                return nameMatch || nameKanaMatch || memberIdMatch;
            });
        },
        [],
    );

    const applyAllFilters = useCallback(
        (
            customers: CustomerDisplay[],
            keyword: string,
            selectedBranch: BranchFilterValue,
        ): CustomerDisplay[] => {
            const searchFiltered = applySearchFilter(customers, keyword);
            if (selectedBranch === null) return searchFiltered;
            if (selectedBranch === 'UNSET') {
                return searchFiltered.filter((c) => c.branch_id == null);
            }
            return searchFiltered.filter((c) => c.branch_id === selectedBranch);
        },
        [applySearchFilter],
    );

    const handleClickFilter = useCallback((filterName: string) => {
        const rowCustomers = lessonStore.customersPerRow(filterName);
        setFilteredCustomers(applyAllFilters(rowCustomers, searchText, branchFilter));
        setSelectedFilterName(filterName);
    }, [lessonStore, applyAllFilters, searchText, branchFilter]);

    const handleSearch = useCallback(() => {
        const baseCustomers = lessonStore.customersPerRow(selectedFilterName);
        setFilteredCustomers(applyAllFilters(baseCustomers, searchText, branchFilter));
    }, [searchText, lessonStore, selectedFilterName, applyAllFilters, branchFilter]);

    const [isDisabledReloadButton, setIsDisabledReloadButton] = useState<boolean>(false);
    const handleClickReload = async (): Promise<void> => {
        try {
            setIsDisabledReloadButton(true);
            // リロード時はコーチ選択状態を保持するように
            const currentSelectedCoach = tempoStore.selectedCoach;

            await Promise.all([
                tempoStore.fetchAllStaffs(),
                lessonStore.fetchAllCustomers(AuthStore.tempoId),
                tempoStore.fetchAllLessons(),
                tempoStore.fetchEnqueteItems(),
            ]);

            if (currentSelectedCoach) {
                tempoStore.selectCoach(currentSelectedCoach.id);
            }
        } finally {
            setIsDisabledReloadButton(false);
        }
    };

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        lessonStore.fetchAllCustomers(AuthStore.tempoId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const base = lessonStore.customersPerRow(selectedFilterName);
        setFilteredCustomers(applyAllFilters(base, searchText, branchFilter));
    }, [allCustomers, lessonStore, selectedFilterName, applyAllFilters, searchText, branchFilter]);

    const runSearchWithBranch = useCallback(
        (branchOverride?: BranchFilterValue) => {
            const effectiveBranch = branchOverride !== undefined ? branchOverride : branchFilter;
            const base = lessonStore.customersPerRow(selectedFilterName);
            setFilteredCustomers(applyAllFilters(base, searchText, effectiveBranch));
        },
        [lessonStore, selectedFilterName, searchText, branchFilter, applyAllFilters],
    );

    const handleBranchChange = useCallback(
        (_: unknown, value: { id: BranchFilterValue; label: string } | null) => {
            const next = value?.id ?? null;
            setBranchFilter(next);
            runSearchWithBranch(next);
        },
        [runSearchWithBranch],
    );

    const filteredCounts = useMemo(() => {
        const counts: Record<string, number> = {
            ALL: applyAllFilters(allCustomers, searchText, branchFilter).length,
        };
        lessonStore.rows.forEach((row) => {
            counts[row] = applyAllFilters(
                lessonStore.customersPerRow(row),
                searchText,
                branchFilter,
            ).length;
        });
        return counts;
    }, [allCustomers, lessonStore, applyAllFilters, searchText, branchFilter]);

    return (
        <Page headerIcon={HeaderIcon} title='レッスンノート' onClickReload={handleClickReload} isDisabledReloadButton={isDisabledReloadButton}>
            <PageLayout>
                <CoachProfile />
                <WillLessonCustomers />
                <SearchBoxWrapper>
                    <SearchInput
                        id='lesson-customer-search'
                        placeholder='氏名、氏名かな、または会員IDを入力'
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        fullWidth
                        style={{ width: '100%', flex: 1 }}
                    />
                    <SearchButton onClick={handleSearch}>
                        検索
                    </SearchButton>
                    {branches.length > 0 && (
                        <BranchSelect>
                            <Autocomplete
                                options={branchOptions}
                                getOptionLabel={(o: { id: BranchFilterValue; label: string }) => o.label}
                                value={currentBranchOption}
                                onChange={handleBranchChange}
                                renderInput={(params: React.ComponentProps<typeof TextField>) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        size='small'
                                        placeholder='拠点'
                                        style={{ backgroundColor: '#f1f0f8' }}
                                    />
                                )}
                            />
                        </BranchSelect>
                    )}
                </SearchBoxWrapper>
                <CustomerSelectWrapper>
                    <CustomerNameFilter
                        onClick={handleClickFilter}
                        filteredCounts={filteredCounts}
                    ></CustomerNameFilter>
                    <CustomerGridViewer
                        customers={filteredCustomers.concat()}
                    ></CustomerGridViewer>
                </CustomerSelectWrapper>
            </PageLayout>
        </Page>
    );
};

export default observer(LessonListPage);
